// Central navigation + module registry.
// NAV mirrors the original Bakery ERP app (legacy host retired; this app is now standalone)
// EXACTLY: 3 groups, 23 items, exact names + order.
// HIDDEN_ITEMS stay routable (reachable by URL / as tabs) but are NOT shown in
// the sidebar. They will be surfaced as tabs inside their reference parent page
// during page-by-page alignment. No functionality is removed.
//
// item shape:
//   { label, path, testId, roles?, icon?,
//     endpoint, dataPath?, idKey?, columns?, searchKeys?, filters?,
//     exportEndpoint?, exportLabel?, custom?, clientFilter?, mobileNote? }
//
// `roles` empty => visible to everyone signed in. `super_admin` always sees all.

import {
  LayoutDashboard, ShoppingCart, Factory, CalendarDays, ClipboardList,
  Package, BookOpen, GitBranch, Calculator, Tags, Wheat, TrendingUp,
  History, Truck, Boxes, FileText, ReceiptText, Warehouse, Users,
  ShieldCheck, Building2, Settings, ScrollText, PackageCheck, Send,
  Store, BadgeIndianRupee, Upload, FileSpreadsheet, ListChecks,
  Thermometer, Sparkles, Wrench, AlertTriangle, ClipboardCheck,
  FileBarChart, UserCog, DatabaseZap, FlaskConical, Eye, Undo2, CalendarClock,
} from "lucide-react";

// column type shorthands
const T = (key, label) => ({ key, label, type: "text" });
const D = (key, label) => ({ key, label, type: "date" });
const DT = (key, label) => ({ key, label, type: "datetime" });
const N = (key, label) => ({ key, label, type: "number" });
const C = (key, label) => ({ key, label, type: "currency" });
const P = (key, label) => ({ key, label, type: "percent" });
const B = (key, label) => ({ key, label, type: "badge" });
const M = (key, label) => ({ key, label, type: "mono" });
const BOOL = (key, label) => ({ key, label, type: "bool" });

// ---- List configs reused across NAV + HIDDEN ----
const CFG = {
  orders: {
    label: "Orders", path: "/orders", testId: "orders", icon: ShoppingCart,
    roles: ["management", "accounts", "stores_manager", "production_manager"], custom: "orders",
    endpoint: "/orders", idKey: "id",
    columns: [M("orderNo", "Order #"), T("buyerName", "Buyer"), B("status", "Status"),
      D("orderDate", "Order Date"), D("deliveryDate", "Delivery"), C("totalAmount", "Total")],
    searchKeys: ["orderNo", "buyerName"], filters: [{ key: "status", label: "Status" }],
  },
  salesBills: {
    label: "Sales Bills", path: "/sales-bills", testId: "sales-bills", icon: FileSpreadsheet,
    roles: ["management", "accounts"], custom: "sales-bills",
    endpoint: "/sales-bills", idKey: "id",
    columns: [M("billNo", "Invoice #"), T("buyerName", "Buyer"), B("status", "Status"),
      D("billDate", "Date"), C("totalAmount", "Total"), T("source", "Source")],
    searchKeys: ["billNo", "externalInvoiceNumber", "buyerName"], filters: [{ key: "status", label: "Status" }, { key: "source", label: "Source" }],
  },
  storeInventory: {
    label: "Store / Inventory", path: "/raw-material-inventory", testId: "raw-material-inventory", icon: Warehouse,
    roles: ["management", "stores_manager", "store_executive", "purchase_manager", "production_manager"],
    custom: "store-inventory",
  },
  storePurchase: {
    label: "Store Purchase", path: "/purchase-requests", testId: "purchase-requests", icon: Store,
    roles: ["management", "purchase_manager", "stores_manager", "store_executive"],
    endpoint: "/purchase-requests", idKey: "id",
    columns: [M("prNo", "PR #"), B("status", "Status"), T("requestedBy", "Requested By"),
      N("itemCount", "Items"), D("createdAt", "Created")],
    searchKeys: ["prNo"], filters: [{ key: "status", label: "Status" }],
  },
  grn: {
    label: "Goods Receipt / QA", path: "/grn", testId: "grn", icon: PackageCheck,
    roles: ["management", "stores_manager", "store_executive", "purchase_manager"],
    endpoint: "/grns", idKey: "id",
    columns: [M("grnNo", "GRN #"), T("supplierName", "Supplier"), M("poNo", "PO #"),
      B("status", "Status"), B("qaStatus", "QA"), D("receivedDate", "Received")],
    searchKeys: ["grnNo", "supplierName", "poNo"], filters: [{ key: "status", label: "Status" }],
  },
  purchaseBills: {
    label: "Purchase Bills", path: "/purchase-bills", testId: "purchase-bills", icon: ReceiptText,
    roles: ["management", "purchase_manager", "accounts", "stores_manager", "store_executive"], custom: "purchase-bills",
    endpoint: "/purchase-bills", idKey: "id",
    deleteEndpoint: "/purchase-bills", deleteRoles: ["accounts", "management"],
    columns: [M("billNo", "Bill #"), T("supplierName", "Supplier"), B("status", "Status"),
      D("billDate", "Date"), C("totalAmount", "Total"), T("source", "Source")],
    searchKeys: ["billNo", "supplierName"], filters: [{ key: "status", label: "Status" }],
  },
  dispatchDelivery: {
    label: "Dispatch & Delivery", path: "/dispatch", testId: "dispatch", icon: Truck,
    roles: ["management", "stores_manager", "store_executive"], custom: "dispatch",
    endpoint: "/dispatch/orders", idKey: "id",
    columns: [M("orderNo", "Order #"), T("buyerName", "Buyer"), B("status", "Status"),
      D("deliveryDate", "Delivery"), N("itemCount", "Items")],
    searchKeys: ["orderNo", "buyerName"], filters: [{ key: "status", label: "Status" }],
  },
  auditHistory: {
    label: "Audit History / Reports", path: "/audit-history", testId: "audit-history", icon: History,
    roles: ["management", "qa_manager", "auditor"],
    endpoint: "/admin/audit-events", idKey: "id",
    columns: [DT("dateTime", "When"), T("actorEmail", "User"), B("actorRole", "Role"),
      B("action", "Action"), T("resource", "Resource"), T("ip", "IP")],
    searchKeys: ["actorEmail", "action", "resource"],
    filters: [{ key: "action", label: "Action" }, { key: "resource", label: "Resource" }],
  },
  products: {
    label: "Products", path: "/products", testId: "products", icon: Package,
    roles: ["management", "production_manager", "qa_manager", "accounts"], custom: "products",
    endpoint: "/products", idKey: "id",
    columns: [M("productId", "Product ID"), T("name", "Name"), B("productType", "Type"), B("category", "Category"),
      T("subcategory", "Sub-category"), M("sku", "SKU"), C("sellingPrice", "Selling"),
      C("mrp", "MRP"), C("costPrice", "Cost"), BOOL("active", "Active")],
    searchKeys: ["productId", "name", "sku"], filters: [{ key: "productType", label: "Type" }, { key: "category", label: "Category" }],
  },
  suppliers: {
    label: "Suppliers", path: "/suppliers", testId: "suppliers", icon: Building2,
    roles: ["management", "purchase_manager", "stores_manager", "store_executive", "accounts"],
    endpoint: "/suppliers", idKey: "id",
    columns: [T("name", "Name"), M("code", "Code"), T("contactPerson", "Contact"), T("phone", "Phone"),
      T("email", "Email"), M("gstin", "GSTIN"), N("leadTimeDays", "Lead (d)"), BOOL("active", "Active")],
    searchKeys: ["name", "gstin", "email"],
  },
  buyers: {
    label: "Buyers", path: "/buyers", testId: "buyers", icon: Users,
    roles: ["management", "accounts"],
    endpoint: "/buyers", idKey: "id",
    columns: [M("buyerId", "Buyer ID"), T("parentCompany", "Parent Company"), T("name", "Store / Branch"),
      M("gstin", "GSTIN"), T("contactPerson", "Contact"), T("mobile", "Mobile"), T("email", "Email"),
      T("city", "City"), BOOL("active", "Active")],
    searchKeys: ["buyerId", "name", "parentCompany", "gstin", "city"],
  },
  users: {
    label: "Users & Roles", path: "/users", testId: "users", icon: UserCog,
    roles: ["super_admin"], custom: "users",
    endpoint: "/admin/users", idKey: "id",
    columns: [T("name", "Name"), T("email", "Email"), B("role", "Role"),
      T("department", "Department"), T("designation", "Designation"), BOOL("active", "Active"),
      DT("lastLogin", "Last Login")],
    searchKeys: ["name", "email", "department"], filters: [{ key: "role", label: "Role" }],
  },
  recipeApprovals: {
    label: "Recipe Approvals", path: "/recipe-approvals", testId: "recipe-approvals", icon: GitBranch,
    roles: ["management", "production_manager", "qa_manager"], custom: "recipe-approvals",
    endpoint: "/recipes/approvals", idKey: "id",
    columns: [M("recipeNumber", "Recipe ID"), T("productName", "Product"), N("version", "Version"),
      B("status", "Status"), DT("submittedAt", "Submitted")],
    searchKeys: ["recipeNumber", "productName", "name"],
  },
};

// ---- Item definitions that were previously hidden, now surfaced under departments ----
const ITEM = {
  productionPlan: { label: "Production Plans", path: "/production-plans", testId: "production-plans", icon: Factory,
    roles: ["management", "production_manager", "production_supervisor"], custom: "production-plans" },
  productionCalendar: { label: "Production Calendar", path: "/production-calendar", testId: "production-calendar", icon: CalendarDays,
    roles: ["management", "production_manager", "production_supervisor"], custom: "production-calendar" },
  productionBatches: { label: "Production Runs / Batches", path: "/production-batches", testId: "production-batches", icon: ClipboardList,
    roles: ["management", "production_manager", "production_supervisor"], custom: "production-batches" },
  finishedGoods: { label: "Finished Goods Inventory", path: "/finished-goods", testId: "finished-goods", icon: Boxes,
    roles: ["management", "stores_manager", "store_executive", "production_manager"], custom: "finished-goods" },
  tallyUpload: { label: "Tally Sales Import", path: "/tally-upload", testId: "tally-upload", icon: Upload,
    roles: ["management", "accounts"], custom: "tally-upload" },
  buyerPoUpload: { label: "Buyer PO Upload", path: "/buyer-po-upload", testId: "buyer-po-upload", icon: Upload,
    roles: ["management", "accounts"], custom: "buyer-po-upload" },
  salesReturns: { label: "Sales Returns", path: "/sales-returns", testId: "sales-returns", icon: Undo2,
    roles: ["management", "accounts", "sales"], custom: "sales-returns" },
  salesReports: { label: "Sales Reports", path: "/sales-reports", testId: "sales-reports", icon: FileBarChart,
    roles: ["management", "accounts", "qa_manager", "production_manager", "purchase_manager"], custom: "sales-reports" },
  purchaseReports: { label: "Purchase Reports", path: "/purchase-reports", testId: "purchase-reports", icon: FileBarChart,
    roles: ["management", "accounts", "purchase_manager", "stores_manager", "store_executive"], custom: "purchase-reports" },
  buyerPrices: { label: "Buyer Price Master", path: "/buyer-prices", testId: "buyer-prices", icon: BadgeIndianRupee,
    roles: ["management", "accounts"], endpoint: "/buyer-prices", idKey: "id",
    columns: [T("buyerName", "Buyer"), T("productName", "Product"), C("price", "Price"),
      N("moq", "MOQ"), P("discount", "Discount"), P("gstPct", "GST"),
      D("effectiveFrom", "Effective"), BOOL("active", "Active")],
    searchKeys: ["buyerName", "productName"] },
  supplierStatement: { label: "Supplier Statement", path: "/supplier-statement", testId: "supplier-statement", icon: BadgeIndianRupee,
    roles: ["management", "accounts", "purchase_manager"], custom: "supplier-statement" },
  purchaseOrders: { label: "Purchase Orders", path: "/purchase-orders", testId: "purchase-orders", icon: FileText,
    roles: ["management", "purchase_manager", "stores_manager", "accounts"], custom: "purchase-orders",
    endpoint: "/purchase-orders", idKey: "id",
    columns: [M("poNo", "PO #"), T("supplierName", "Supplier"), B("status", "Status"),
      D("poDate", "Date"), C("totalAmount", "Total"), P("gstAmount", "GST")],
    searchKeys: ["poNo", "supplierName"], filters: [{ key: "status", label: "Status" }] },
  recipes: { label: "Recipe Master", path: "/recipes", testId: "recipes", icon: BookOpen,
    roles: ["management", "production_manager", "qa_manager"], custom: "recipes",
    endpoint: "/recipes", idKey: "id",
    columns: [M("recipeNumber", "Recipe ID"), T("productName", "Product"), T("name", "Name"),
      N("version", "Version"), B("status", "Status"), N("yield", "Yield"), DT("updatedAt", "Updated")],
    searchKeys: ["recipeNumber", "name", "productName"], filters: [{ key: "status", label: "Status" }] },
  costing: { label: "Costing", path: "/costing", testId: "costing", icon: Calculator,
    roles: ["management", "accounts", "production_manager"], custom: "costing" },
  ingredients: { label: "Ingredient Master", path: "/ingredients", testId: "ingredients", icon: Wheat,
    roles: ["management", "stores_manager", "store_executive", "purchase_manager", "production_manager"],
    endpoint: "/ingredients", idKey: "id",
    columns: [M("ingredientId", "ID"), T("name", "Name"), B("category", "Category"),
      T("brand", "Brand"), T("unit", "Unit"), N("packingSize", "Pack Size"),
      C("rate", "Rate"), B("priceType", "Price Type"), BOOL("active", "Active")],
    searchKeys: ["ingredientId", "name", "brand", "category"],
    filters: [{ key: "category", label: "Category" }, { key: "priceType", label: "Price Type" }],
    exportEndpoint: "/ingredients/export", exportLabel: "Export Master" },
  ingredientPrices: { label: "Ingredient Prices", path: "/ingredient-prices", testId: "ingredient-prices", icon: TrendingUp,
    roles: ["management", "purchase_manager", "accounts", "stores_manager"],
    endpoint: "/ingredients", idKey: "id",
    columns: [M("ingredientId", "ID"), T("name", "Name"), B("priceType", "Price Type"),
      C("rate", "Current Rate"), C("purchasePrice", "Purchase Price"), P("gstPct", "GST %"),
      T("lastInvoiceSupplier", "Last Supplier"), T("lastInvoiceDate", "Last Invoice")],
    searchKeys: ["ingredientId", "name"], filters: [{ key: "priceType", label: "Price Type" }] },
  qaAudits: { label: "QA Audits", path: "/qa-audits", testId: "qa-audits", icon: ClipboardCheck,
    roles: ["management", "qa_manager", "qa_executive", "auditor"], custom: "quality-audits",
    endpoint: "/audits", idKey: "id",
    columns: [T("bakeryName", "Unit"), T("auditType", "Type"), T("auditorName", "Auditor"),
      B("status", "Status"), P("overallScore", "Score"), N("criticalCount", "Critical"),
      N("failedCount", "Failed"), DT("dateTime", "Date")],
    searchKeys: ["bakeryName", "auditorName", "auditType"],
    filters: [{ key: "status", label: "Status" }, { key: "auditType", label: "Type" }] },
  fssaiChecks: { label: "FSSAI / Compliance", path: "/fssai-checks", testId: "fssai-checks", icon: ShieldCheck,
    roles: ["management", "qa_manager", "qa_executive", "auditor"], custom: "quality-audits",
    endpoint: "/audits", idKey: "id", clientFilter: { field: "auditType", value: "FSSAI" }, mobileNote: true,
    columns: [T("bakeryName", "Unit"), T("auditorName", "Auditor"), B("status", "Status"),
      P("overallScore", "Score"), DT("dateTime", "Date")], searchKeys: ["bakeryName", "auditorName"] },
  hygiene: { label: "Hygiene / Cleaning", path: "/hygiene", testId: "hygiene", icon: Sparkles,
    roles: ["management", "qa_manager", "qa_executive"], custom: "quality-audits",
    endpoint: "/audits", idKey: "id", clientFilter: { field: "auditType", value: "Hygiene" }, mobileNote: true,
    columns: [T("bakeryName", "Unit"), T("auditorName", "Auditor"), B("status", "Status"),
      P("overallScore", "Score"), DT("dateTime", "Date")], searchKeys: ["bakeryName", "auditorName"] },
  temperature: { label: "Temperature Checks", path: "/temperature", testId: "temperature", icon: Thermometer,
    roles: ["management", "qa_manager", "qa_executive"], custom: "quality-audits",
    endpoint: "/audits", idKey: "id", clientFilter: { field: "auditType", value: "Temperature" }, mobileNote: true,
    columns: [T("bakeryName", "Unit"), T("auditorName", "Auditor"), B("status", "Status"),
      DT("dateTime", "Date")], searchKeys: ["bakeryName", "auditorName"] },
  productionQa: { label: "Production QA", path: "/production-qa", testId: "production-qa", icon: FlaskConical,
    roles: ["management", "qa_manager", "qa_executive", "production_manager"], custom: "quality-audits",
    endpoint: "/audits", idKey: "id", clientFilter: { field: "auditType", value: "Production" }, mobileNote: true,
    columns: [T("bakeryName", "Unit"), T("auditorName", "Auditor"), B("status", "Status"),
      P("overallScore", "Score"), DT("dateTime", "Date")], searchKeys: ["bakeryName", "auditorName"] },
  maintenance: { label: "Maintenance Checks", path: "/maintenance", testId: "maintenance", icon: Wrench,
    roles: ["management", "maintenance_manager", "maintenance_executive"], custom: "quality-audits",
    endpoint: "/audits", idKey: "id", clientFilter: { field: "auditType", value: "Maintenance" }, mobileNote: true,
    columns: [T("bakeryName", "Unit"), T("auditorName", "Auditor"), B("status", "Status"),
      DT("dateTime", "Date")], searchKeys: ["bakeryName", "auditorName"] },
  dailyAudit: { label: "Daily Audit", path: "/daily-audit", testId: "daily-audit", icon: ClipboardCheck,
    roles: ["management", "qa_manager", "qa_executive", "auditor"], custom: "daily-audit" },
  weeklyAudit: { label: "Weekly Audit", path: "/weekly-audit", testId: "weekly-audit", icon: CalendarDays,
    roles: ["management", "qa_manager", "qa_executive", "auditor"], custom: "weekly-audit" },
  capa: { label: "CAPA / Non-Conformity", path: "/capa", testId: "capa", icon: AlertTriangle,
    roles: ["management", "qa_manager", "qa_executive", "auditor"], custom: "capa",
    endpoint: "/capa", idKey: "id",
    columns: [M("capaId", "CAPA #"), T("title", "Title"), B("status", "Status"),
      B("priority", "Priority"), T("assignedTo", "Assigned"), D("dueDate", "Due"), D("createdAt", "Created")],
    searchKeys: ["title", "capaId"], filters: [{ key: "status", label: "Status" }, { key: "priority", label: "Priority" }] },
  qualityAuditReport: { label: "Quality & Compliance Audit Report", path: "/quality-audit-report", testId: "quality-audit-report",
    icon: FileBarChart, roles: ["management", "qa_manager", "qa_executive", "auditor"], custom: "quality-audit-report" },
  auditActionReport: { label: "Audit & Compliance Action Report", path: "/audit-action-report", testId: "audit-action-report",
    icon: ClipboardList, roles: ["management", "qa_manager", "qa_executive", "auditor"], custom: "audit-action-report" },
  customerReceipts: { label: "Customer Receipts", path: "/customer-receipts", testId: "customer-receipts", icon: BadgeIndianRupee,
    roles: ["management", "accounts"], custom: "customer-receipts" },
  buyerLedger: { label: "Buyer Ledger", path: "/buyer-ledger", testId: "buyer-ledger", icon: FileBarChart,
    roles: ["management", "accounts"], custom: "buyer-ledger" },
  creditNotes: { label: "Credit Notes", path: "/credit-notes", testId: "credit-notes", icon: ReceiptText,
    roles: ["management", "accounts"], custom: "credit-notes" },
  reports: { label: "Reports", path: "/reports", testId: "reports", icon: FileBarChart,
    roles: ["management", "accounts", "qa_manager", "production_manager", "purchase_manager"], custom: "reports" },
  reportSchedules: { label: "Report Scheduling", path: "/report-schedules", testId: "report-schedules", icon: FileBarChart,
    roles: ["management"], custom: "report-schedules" },
  companyProfile: { label: "Company Profile", path: "/company-profile", testId: "company-profile", icon: Building2,
    roles: ["management"], custom: "company-profile" },
  settings: { label: "Cost & System Settings", path: "/settings-ingredients", testId: "settings-ingredients", icon: Settings,
    roles: ["management"], custom: "settings-ingredients" },
  masterData: { label: "Master Data Setup", path: "/master-data", testId: "master-data", icon: DatabaseZap,
    roles: ["management"], custom: "master-data-setup", endpoint: "/master-data-import/batches", idKey: "id" },
  historicalImport: { label: "Historical Data Import", path: "/historical-data-import", testId: "historical-data-import", icon: Upload,
    roles: ["super_admin", "management", "stores_manager", "store_executive"], custom: "historical-data-import" },
  historicalStock: { label: "Historical Stock", path: "/historical-stock", testId: "historical-stock", icon: Boxes,
    roles: ["super_admin", "management", "stores_manager", "store_executive"], custom: "historical-stock" },
  auditLogs: { label: "Audit / Activity Log", path: "/audit-logs", testId: "audit-logs", icon: ScrollText,
    roles: ["management", "auditor"],
    endpoint: "/admin/audit-events", idKey: "id",
    columns: [DT("dateTime", "When"), T("actorEmail", "User"), B("actorRole", "Role"),
      B("action", "Action"), T("resource", "Resource"), T("resourceId", "Resource ID"), T("ip", "IP")],
    searchKeys: ["actorEmail", "action", "resource"],
    filters: [{ key: "action", label: "Action" }, { key: "resource", label: "Resource" }] },
  verificationCleanup: { label: "Verification Records", path: "/verification-cleanup", testId: "verification-cleanup",
    icon: FlaskConical, roles: ["management"], custom: "verification-cleanup" },
  duplicateAudit: { label: "Duplicate Audit", path: "/duplicate-audit", testId: "duplicate-audit",
    icon: AlertTriangle, roles: ["management", "accounts"], custom: "duplicate-audit" },
};

export const NAV = [
  {
    group: "Dashboard",
    items: [
      { label: "Dashboard", path: "/", testId: "dashboard", icon: LayoutDashboard, custom: "dashboard" },
    ],
  },
  {
    group: "Orders & Sales",
    items: [
      { ...CFG.salesBills, label: "Sales Bills / Invoices" },
      CFG.orders,
      ITEM.buyerPoUpload,
      ITEM.tallyUpload,
      CFG.dispatchDelivery,
      ITEM.salesReturns,
      { ...CFG.buyers, label: "Buyers" },
      ITEM.buyerPrices,
      ITEM.salesReports,
    ],
  },
  {
    group: "Production",
    items: [
      ITEM.productionPlan,
      ITEM.productionCalendar,
      ITEM.productionBatches,
      ITEM.finishedGoods,
    ],
  },
  {
    group: "Stores & Purchase",
    items: [
      { ...CFG.storeInventory, label: "Raw Material Inventory" },
      CFG.suppliers,
      { ...CFG.storePurchase, label: "Purchase Requests" },
      ITEM.purchaseOrders,
      { ...CFG.grn, label: "GRN / Receiving" },
      { ...CFG.purchaseBills, label: "Purchase Bills / OCR" },
      ITEM.supplierStatement,
    ],
  },
  {
    group: "Recipes & Products",
    items: [
      { ...CFG.products, label: "Product Master" },
      ITEM.recipes,
      CFG.recipeApprovals,
      ITEM.costing,
      ITEM.ingredients,
      ITEM.ingredientPrices,
    ],
  },
  {
    group: "Quality & Compliance",
    items: [
      ITEM.qaAudits,
      ITEM.fssaiChecks,
      ITEM.hygiene,
      ITEM.temperature,
      ITEM.productionQa,
      ITEM.maintenance,
      ITEM.dailyAudit,
      ITEM.weeklyAudit,
      ITEM.capa,
      ITEM.qualityAuditReport,
      ITEM.auditActionReport,
    ],
  },
  {
    group: "Accounts",
    items: [
      ITEM.customerReceipts,
      ITEM.buyerLedger,
      ITEM.creditNotes,
      { label: "Receivables Due", path: "/receivables-due", testId: "receivables-due", icon: CalendarClock,
        roles: ["management", "accounts"], custom: "receivables-due" },
    ],
  },
  {
    group: "Reports",
    items: [
      ITEM.reports,
      ITEM.purchaseReports,
      ITEM.reportSchedules,
    ],
  },
  {
    group: "Settings / Admin",
    items: [
      ITEM.companyProfile,
      CFG.users,
      ITEM.settings,
      ITEM.masterData,
      ITEM.historicalImport,
      ITEM.historicalStock,
      ITEM.auditLogs,
      ITEM.duplicateAudit,
      ITEM.verificationCleanup,
    ],
  },
];

// Routes that remain reachable (URL / sub-views launched from a parent page) but
// are NOT shown in the sidebar to avoid duplicate navigation entries.
export const HIDDEN_ITEMS = [
  // Audit trail — same data as Settings ▸ Audit / Activity Log
  { ...CFG.auditHistory },
  // Sub-flows of Dispatch & Delivery
  { label: "Dispatch Preview", path: "/dispatch-preview", testId: "dispatch-preview", icon: Eye,
    roles: ["management", "stores_manager", "store_executive", "production_manager"], custom: "dispatch-preview" },
  { label: "Delivery / POD", path: "/delivery", testId: "delivery", icon: Truck,
    roles: ["management", "stores_manager", "store_executive"],
    endpoint: "/dispatch/delivered", idKey: "id",
    columns: [M("orderNo", "Order #"), T("buyerName", "Buyer"), B("status", "Status"),
      DT("deliveredAt", "Delivered At"), T("receivedBy", "Received By")],
    searchKeys: ["orderNo", "buyerName"] },
  // OCR scan entry launched from Purchase Bills / OCR
  { label: "Supplier Bill Upload", path: "/supplier-bill-upload", testId: "supplier-bill-upload", icon: Upload,
    roles: ["management", "purchase_manager", "accounts", "stores_manager", "store_executive"], custom: "supplier-bill-upload" },
  // NC list is a filtered view of CAPA / Non-Conformity
  { label: "Nonconformity", path: "/nonconformity", testId: "nonconformity", icon: AlertTriangle,
    roles: ["management", "qa_manager", "qa_executive", "auditor"], custom: "capa",
    endpoint: "/capa", idKey: "id", clientFilter: { field: "type", value: "NC" }, mobileNote: true,
    columns: [M("capaId", "NC #"), T("title", "Title"), B("status", "Status"),
      B("priority", "Priority"), T("assignedTo", "Assigned"), D("dueDate", "Due")], searchKeys: ["title", "capaId"] },
];

// Flatten leaves for router (sidebar items + hidden-but-routable items).
export const ALL_ITEMS = [...NAV.flatMap((g) => g.items), ...HIDDEN_ITEMS];

export function findItemByPath(path) {
  return ALL_ITEMS.find((i) => i.path === path);
}

// Suppress unused-import lint for icons kept for future alignment work.
export const _ICONS = { Tags, Send, ListChecks };
